/**
 * The CP console's client for private release channels and their shares (private-release-channels
 * WS-5). A private channel belongs to one developer's namespace (`private/<handle>/<name>`), routes
 * on a derived key `p-<12 hex>`, and reaches a tenant only through a share the tenant accepts on its
 * data plane (overview L-1 to L-5).
 *
 * Every route here is release-engine's, written by WS-1 and named in `RELEASE_CHANNEL_ROUTES` in
 * `@zion/sdk-contracts`. Each path builder returns a literal that equals its contract member with
 * the `:id` filled, and `private-channels-api-paths.test.ts` pins both. The literals stay literal
 * so the dev fixture census in `src/dev/__tests__/fixture-routes-answer.test.ts` can see them.
 *
 * The panes decide which controls to show from the permission constants below. Release-engine
 * decides who may act: the permission guard, then the owner rule `canActOnOwned` (defaults row 4).
 * A per-id route on a row the caller may not act on answers 404 (R-013).
 *
 * Every fetcher sends one request through `api` from `platform/http/client`. A non-2xx rejects with
 * `ApiError`, holding `status` and the server's body; the caller renders the body's sentence
 * through `displayMessage`. No fetcher retries, caches or writes to storage.
 */
import type {
  CappedResponse,
  ChannelShare,
  CpPermission,
  CreateChannelShareRequest,
  CreatePrivateChannelRequest,
  ReleaseChannel,
  ShareTarget,
  UpdateReleaseChannelRequest,
} from '@zion/sdk-contracts';
import { api } from '../platform/http/client';

/**
 * Gates the Private channels list and detail panes, the manifest entry and the share list:
 * `GET /v1/channels/private`, `GET /v1/channels/private/:id` and `GET /v1/channels/shares` (R-015).
 * The routes answer the caller's own namespace, or every namespace under manage-all.
 */
export const PRIVATE_CHANNEL_READ_PERMISSION = 'cp:channel:private:read' satisfies CpPermission;

/** Gates New channel, `POST /v1/channels/private`. */
export const PRIVATE_CHANNEL_CREATE_PERMISSION = 'cp:channel:private:create' satisfies CpPermission;

/** Gates Rename on a private channel, `PATCH /v1/channels/private/:id` (R-005). */
export const PRIVATE_CHANNEL_UPDATE_PERMISSION = 'cp:channel:private:update' satisfies CpPermission;

/** Gates Delete, `DELETE /v1/channels/private/:id`. */
export const PRIVATE_CHANNEL_DELETE_PERMISSION = 'cp:channel:private:delete' satisfies CpPermission;

/**
 * Gates Share with tenants and Revoke: `POST /v1/channels/shares`, `DELETE /v1/channels/shares/:id`
 * and the tenant picker's `GET /v1/channels/share-targets` (R-015).
 */
export const PRIVATE_CHANNEL_SHARE_PERMISSION = 'cp:channel:private:share' satisfies CpPermission;

/**
 * The platform operator's override over every namespace, which release-engine reads inside
 * `canActOnOwned`. No pane reads it: release-engine answers 404 on a row the caller cannot act on
 * (R-013), so the rows a pane renders are already the ones the caller may act on. It stays for the
 * AP-6 census in `private-channels-api-paths.test.ts`, which pins every permission this module
 * names to its literal and to `ALL_CP_PERMISSIONS`.
 */
export const PRIVATE_CHANNEL_MANAGE_ALL_PERMISSION =
  'cp:channel:private:manage-all' satisfies CpPermission;

/** `/v1/channels/private`, `RELEASE_CHANNEL_ROUTES.listPrivate` and `.createPrivate`. Pure. */
export function privateChannelsPath(): string {
  return '/v1/channels/private';
}


/**
 * A server-minted id made safe to place in one URL path segment. The allowlist is
 * `[A-Za-z0-9._-]`, 1 to 200 characters, and the segments `.` and `..` are refused by name:
 * `encodeURIComponent` leaves both unchanged and `new URL()` collapses them, so `..` would turn
 * `/v1/channels/private/..` into `/v1/channels/` (Copilot on #3117; the CLI's `pathSegment` does
 * the same). The route then sees `/`, `?` and `#` percent-encoded and nothing else. Pure.
 *
 * @throws Error `<what> is not a valid path segment` on a refused value.
 */
export function pathSegment(value: string, what: string): string {
  if (!/^[A-Za-z0-9._-]{1,200}$/.test(value) || value === '.' || value === '..') {
    throw new Error(`${what} is not a valid path segment`);
  }
  return encodeURIComponent(value);
}

/**
 * `/v1/channels/private/:id`, `RELEASE_CHANNEL_ROUTES.getPrivate`, `.updatePrivate` and
 * `.deletePrivate`. The id passes through `pathSegment`, so it is one path segment and never a dot
 * segment. Pure.
 */
export function privateChannelPath(channelId: string): string {
  return `/v1/channels/private/${pathSegment(channelId, 'channel id')}`;
}

/** `/v1/channels/shares`, `RELEASE_CHANNEL_ROUTES.createShares` and `.listShares`. Pure. */
export function channelSharesPath(): string {
  return '/v1/channels/shares';
}

/**
 * The share list for one channel with revoked shares included:
 * `/v1/channels/shares?channelId=:id&includeRevoked=true`. The route keeps the shares whose pattern
 * is the channel's slug or its namespace wildcard, written by the channel's owner (WS-1 D-9).
 * `includeRevoked` is the literal `true`, the only spelling the route's strict query accepts besides
 * `false` (WS-1 D-8). Pure.
 */
export function channelSharesForChannelPath(channelId: string): string {
  return `/v1/channels/shares?channelId=${pathSegment(channelId, 'channel id')}&includeRevoked=true`;
}

/** `/v1/channels/shares/:id`, `RELEASE_CHANNEL_ROUTES.revokeShare`, id encoded. Pure. */
export function channelSharePath(shareId: string): string {
  return `/v1/channels/shares/${pathSegment(shareId, 'share id')}`;
}

/** `/v1/channels/share-targets`, `RELEASE_CHANNEL_ROUTES.shareTargets`. Pure. */
export function shareTargetsPath(): string {
  return '/v1/channels/share-targets';
}

/**
 * `GET /v1/channels/private`: the private channels the caller may read, in server order, which the
 * list pane keeps.
 *
 * @returns `{ items, truncated }` (R-032). `truncated` is true when more than `CHANNEL_LIST_CAP`
 *   rows existed and the server cut the list.
 * @throws ApiError 403 without `cp:channel:private:read`.
 */
export function listPrivateChannels(): Promise<CappedResponse<ReleaseChannel>> {
  return api.get<CappedResponse<ReleaseChannel>>(privateChannelsPath());
}

/**
 * `GET /v1/channels/private/:id`: one private channel.
 *
 * @throws ApiError 404 for an unknown id, a public id, or a row the caller may not act on (R-013);
 *   403 without `cp:channel:private:read`.
 */
export function getPrivateChannel(channelId: string): Promise<ReleaseChannel> {
  return api.get<ReleaseChannel>(privateChannelPath(channelId));
}

/**
 * `POST /v1/channels/private`: create a private channel.
 *
 * `body` is the parsed `createPrivateChannelRequestSchema` output, so its slug is canonical: one
 * leading slash already stripped (R-026). Release-engine derives the key, the kind and the owner.
 *
 * @returns the new row with its `p-<12 hex>` key.
 * @throws ApiError 400 on a body the route refuses; 403 without `cp:channel:private:create`, or with
 *   `namespace_not_yours` for another developer's handle; 409 `namespace_owned_by_other`,
 *   `namespace_unclaimed`, `namespace_full` or `slug_taken` (the list `PrivateChannelPort` in the
 *   CLI carries).
 */
export function createPrivateChannel(body: CreatePrivateChannelRequest): Promise<ReleaseChannel> {
  return api.post<ReleaseChannel>(privateChannelsPath(), body);
}

/**
 * `PATCH /v1/channels/private/:id`: rename a private channel or edit its description (R-005).
 * An absent member leaves the column alone and `description: null` clears it. The slug and the key
 * never change.
 *
 * @throws ApiError 400 on a body the route refuses; 403 without `cp:channel:private:update`; 404 for
 *   an unknown id, a public id, or a row the caller may not act on.
 */
export function updatePrivateChannel(
  channelId: string,
  body: UpdateReleaseChannelRequest,
): Promise<ReleaseChannel> {
  return api.patch<ReleaseChannel>(privateChannelPath(channelId), body);
}

/**
 * `DELETE /v1/channels/private/:id`. Resolves on 204.
 *
 * @throws ApiError 403 without `cp:channel:private:delete`; 404 as for the read; 409
 *   `channel_has_pins` while an app version is pinned to it, `channel_has_shares` while an
 *   unrevoked share names its exact slug, `channel_referenced` while another row refers to it.
 */
export function deletePrivateChannel(channelId: string): Promise<void> {
  return api.delete<void>(privateChannelPath(channelId));
}

/**
 * `GET /v1/channels/shares?channelId=:id&includeRevoked=true`: every share covering the channel,
 * revoked ones included, each with its tenant's state (R-002).
 *
 * @returns `{ items, truncated }`; `truncated` is true when the server's cap cut the list.
 * @throws ApiError 400 for a malformed id; 403 without `cp:channel:private:read`.
 */
export function listChannelShares(channelId: string): Promise<CappedResponse<ChannelShare>> {
  return api.get<CappedResponse<ChannelShare>>(channelSharesForChannelPath(channelId));
}

/**
 * `POST /v1/channels/shares`: offer `body.pattern` to every org in `body.orgIds`.
 *
 * `body` is the parsed `createChannelShareRequestSchema` output: the pattern canonical, 1 to
 * `CHANNEL_SHARE_ORG_IDS_MAX` org ids. Sharing an org that already holds a live share of the pattern
 * is idempotent (R-013).
 *
 * @returns one `ChannelShare` per distinct org id.
 * @throws ApiError 400 on a body the route refuses; 403 without `cp:channel:private:share` or for
 *   another developer's namespace; 404 `org_not_found` or `channel_not_found`; 409
 *   `org_share_limit`, `owner_share_limit`, `share_held_by_other_owner`, `namespace_owned_by_other`
 *   or `namespace_unclaimed`.
 */
export function createChannelShares(body: CreateChannelShareRequest): Promise<readonly ChannelShare[]> {
  return api.post<readonly ChannelShare[]>(channelSharesPath(), body);
}

/**
 * `DELETE /v1/channels/shares/:id`: revoke one share. Revoking a revoked share returns it unchanged.
 * The next desired-state envelope drops the offer, and a new share reverses the revoke (L-9).
 *
 * @returns the revoked row, `state: 'revoked'`.
 * @throws ApiError 403 without `cp:channel:private:share`; 404 `share_not_found`, or a share the
 *   caller may not act on.
 */
export function revokeChannelShare(shareId: string): Promise<ChannelShare> {
  return api.delete<ChannelShare>(channelSharePath(shareId));
}

/**
 * `GET /v1/channels/share-targets`: every org with status `active`, as `{ id, slug, name }`, ordered
 * by slug then id (R-015, R-072, WS-1 D-11). It needs `cp:channel:private:share` and no `cp:tenant:read`, so a
 * developer can pick tenants by name.
 *
 * @returns `{ items, truncated }`, capped at `CHANNEL_LIST_CAP`.
 * @throws ApiError 403 without `cp:channel:private:share`.
 */
export function listShareTargets(): Promise<CappedResponse<ShareTarget>> {
  return api.get<CappedResponse<ShareTarget>>(shareTargetsPath());
}
